// src/annihilation/TrainingYard.jsx
// ============================================================
//  ANNIHILATION — Sigfer's Yard (Batch 5's proving ground).
//
//  The first place movement, the Lyt combo, dodge/iframes, and
//  spent skill points are FELT. A training dummy stands in the
//  yard; it swings back slowly so the dodge matters. Batch 6's
//  Whispering Grove replaces this as the real field — the yard
//  stays forever as the safe place to test a build after a
//  respec-less point spend (that's the design point: points are
//  permanent, so Sigfer gives you a yard before you commit).
//
//  ARCHITECTURE: this file renders and owns ENEMY state only.
//  All player logic lives in usePlayerController (the overseer);
//  all damage flows through combat.js inside it. The dummy's
//  bleed ledger uses PlayerController's applyBleedStack/tickBleed
//  so the bleed law lives in exactly one place.
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  usePlayerAnimation,
  MobileDpad,
  MobileActionPad,
} from "./PlayerEntity";
// Being continuity (July 16): you train as what you ARE.
import { AnnihilationPlayer, getBeingPalette } from "./BeingSprite";
// The visual makeover (July 19): every move has its own strike.
import { useAbilityFX, AbilityFXLayer } from "./AbilityFX";
import {
  usePlayerController,
  applyBleedStack,
  tickBleed,
  MAX_BLEED_STACKS,
} from "./PlayerController";
import SkillBar from "./SkillBar";

// ---- yard geometry (tiles) ----------------------------------
const TILE = 48;
const BOUNDS = { minX: 1, maxX: 11, minY: 1, maxY: 7 };
const YARD_W = (BOUNDS.maxX + 2) * TILE;
const YARD_H = (BOUNDS.maxY + 2) * TILE;

const DUMMY_SPAWN = { x: 8, y: 4 };
const DUMMY_BASE = { anima: 60, lyf: 2, attack: 8 };
const DUMMY_RESPAWN_MS = 1600;
const DUMMY_SWING_RANGE = 1.6;
const DUMMY_TELEGRAPH_MS = 800;
const DUMMY_SWING_EVERY_MS = 2600;

let floatSeq = 0;

export default function TrainingYard({ character, onBack }) {
  // ---- enemy state (this file is its one owner) -------------
  const [dummy, setDummy] = useState({
    id: "dummy",
    pos: { ...DUMMY_SPAWN },
    anima: DUMMY_BASE.anima,
    lyf: DUMMY_BASE.lyf,
    bleedStacks: 0,
    alive: true,
    telegraphing: false,
  });
  const dummyRef = useRef(dummy);
  useEffect(() => {
    dummyRef.current = dummy;
  }, [dummy]);

  const [floats, setFloats] = useState([]); // floating damage numbers
  const [feltEvent, setFeltEvent] = useState(null); // dry / dodged text

  const addFloat = useCallback((pos, text, color) => {
    const id = ++floatSeq;
    setFloats((f) => [...f, { id, x: pos.x, y: pos.y, text, color }]);
    setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 900);
  }, []);

  // ---- ability FX (one registry, AbilityFX.js) ---------------
  const { fx, spawnFx } = useAbilityFX();
  const pal = getBeingPalette(character?.beingId);

  // ---- the controller (the overseer) ------------------------
  const ctrl = usePlayerController({
    character,
    startPos: { x: 3, y: 4 },
    bounds: BOUNDS,
    isBlocked: (p) => {
      const d = dummyRef.current;
      return d.alive && p.x === d.pos.x && p.y === d.pos.y;
    },
    getTargets: () => [dummyRef.current],
    onHit: ({ damage, crit, bleedApplied }) => {
      spawnFx("hit_spark", { pos: dummyRef.current.pos, crit, tint: pal });
      setDummy((d) => {
        if (!d.alive) return d;
        let next = { ...d, anima: Math.max(0, d.anima - damage) };
        if (bleedApplied) next = applyBleedStack(next);
        addFloat(
          d.pos,
          crit ? `${damage}!` : `${damage}`,
          crit ? "#ffd24a" : "#e86a5c"
        );
        if (next.anima <= 0) {
          next.alive = false;
          next.telegraphing = false;
          setTimeout(
            () =>
              setDummy({
                id: "dummy",
                pos: { ...DUMMY_SPAWN },
                anima: DUMMY_BASE.anima,
                lyf: DUMMY_BASE.lyf,
                bleedStacks: 0,
                alive: true,
                telegraphing: false,
              }),
            DUMMY_RESPAWN_MS
          );
        }
        return next;
      });
    },
    onEvent: (evt) => {
      if (evt.type === "swing" && evt.lytId) {
        // the ability id IS the registry key (vyren)
        spawnFx(evt.lytId, { pos: evt.origin, facing: evt.facing, step: evt.step, tint: pal });
      }
      if (evt.type === "dodge") {
        spawnFx("dodge_streak", { pos: evt.from, to: evt.to, tint: pal });
      }
      if (evt.type === "dry") {
        spawnFx("lyt_dry", { pos: evt.origin });
        flash("Lyt spent — breathe");
      }
      if (evt.type === "hurt" && evt.dodged) flash("phased!");
      if (evt.type === "swing" && evt.free) flash("Phase Momentum — free swing");
    },
  });

  const flash = (text) => {
    setFeltEvent(text);
    setTimeout(() => setFeltEvent(null), 900);
  };

  // ---- dummy retaliation — the reason dodge exists ----------
  useEffect(() => {
    const t = setInterval(() => {
      const d = dummyRef.current;
      if (!d.alive || d.telegraphing) return;
      const playerPos = { x: ctrl.pos.x, y: ctrl.pos.y };
      const near =
        Math.hypot(playerPos.x - d.pos.x, playerPos.y - d.pos.y) <=
        DUMMY_SWING_RANGE;
      if (!near) return;
      setDummy((cur) => ({ ...cur, telegraphing: true }));
      setTimeout(() => {
        const d2 = dummyRef.current;
        setDummy((cur) => ({ ...cur, telegraphing: false }));
        if (!d2.alive) return;
        // the arm swings whether you're there or not — that's
        // why the dodge matters (unique visual: dummy_sweep)
        spawnFx("dummy_sweep", { pos: d2.pos, toward: ctrlPosRef.current });
        const stillNear =
          Math.hypot(ctrlPosRef.current.x - d2.pos.x, ctrlPosRef.current.y - d2.pos.y) <=
          DUMMY_SWING_RANGE;
        if (!stillNear) return;
        const res = ctrl.takeHit({ attack: DUMMY_BASE.attack });
        addFloat(
          ctrlPosRef.current,
          res.dodged ? "phase" : `-${res.damage}`,
          res.dodged ? "#8fd18a" : "#e86a5c"
        );
      }, DUMMY_TELEGRAPH_MS);
    }, DUMMY_SWING_EVERY_MS);
    return () => clearInterval(t);
    // ctrl.takeHit is stable-enough per mount; pos read via ref below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // live player pos for the delayed dummy swing (avoid stale closure)
  const ctrlPosRef = useRef(ctrl.pos);
  useEffect(() => {
    ctrlPosRef.current = ctrl.pos;
  }, [ctrl.pos]);

  // ---- bleed ticks — once per second, the one bleed law -----
  useEffect(() => {
    const t = setInterval(() => {
      setDummy((d) => {
        if (!d.alive || !d.bleedStacks) return d;
        const { enemy, damage } = tickBleed(d);
        if (damage > 0) addFloat(d.pos, `${damage}`, "#c95c5c");
        if (enemy.anima <= 0 && enemy.alive !== false) {
          setTimeout(
            () =>
              setDummy({
                id: "dummy",
                pos: { ...DUMMY_SPAWN },
                anima: DUMMY_BASE.anima,
                lyf: DUMMY_BASE.lyf,
                bleedStacks: 0,
                alive: true,
                telegraphing: false,
              }),
            DUMMY_RESPAWN_MS
          );
          return { ...enemy, alive: false, telegraphing: false };
        }
        return enemy;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [addFloat]);

  const anim = usePlayerAnimation(ctrl.pos);

  // ---- render ------------------------------------------------
  return (
    <div style={S.void}>
      {/* HUD */}
      <div style={S.hud}>
        <div style={S.hudTitle}>Sigfer's Yard</div>
        <Bar
          label="Anima"
          value={ctrl.anima}
          max={ctrl.animaMax}
          color="#e86a5c"
        />
        <div style={S.lytRow}>
          <span style={S.lytLabel}>Lyt</span>
          {Array.from({ length: ctrl.lytMax }).map((_, i) => (
            <span
              key={i}
              style={{
                ...S.lytPip,
                ...(i < Math.floor(ctrl.lyt) ? S.lytPipFull : null),
              }}
            />
          ))}
          <span style={S.comboText}>
            {ctrl.comboStep
              ? ` combo ${ctrl.comboStep}/${ctrl.comboLength}`
              : ""}
          </span>
        </div>
        <div style={S.barHolder}>
          <SkillBar character={character} compact />
        </div>
      </div>

      {/* the yard */}
      <div style={{ ...S.yard, width: YARD_W, height: YARD_H }}>
        <div style={S.ground} />
        {/* dummy */}
        {dummy.alive ? (
          <div
            style={{
              ...S.dummy,
              left: dummy.pos.x * TILE,
              top: dummy.pos.y * TILE - TILE * 0.6,
              ...(dummy.telegraphing ? S.dummyTelegraph : null),
            }}
          >
            <div style={S.dummyPost} />
            <div style={S.dummyHead} />
            <div style={S.dummyArms} />
            <div style={S.dummyHp}>
              <div
                style={{
                  ...S.dummyHpFill,
                  width: `${(dummy.anima / DUMMY_BASE.anima) * 100}%`,
                }}
              />
            </div>
            {dummy.bleedStacks ? (
              <div style={S.bleedTag}>
                🩸×{dummy.bleedStacks}
                {dummy.bleedStacks >= MAX_BLEED_STACKS ? " MAX" : ""}
              </div>
            ) : null}
          </div>
        ) : (
          <div
            style={{
              ...S.dummyDown,
              left: dummy.pos.x * TILE,
              top: dummy.pos.y * TILE,
            }}
          >
            ✦
          </div>
        )}

        {/* ability visuals — the one registry, world-space */}
        <AbilityFXLayer fx={fx} tile={TILE} />

        {/* floating numbers */}
        {floats.map((f) => (
          <div
            key={f.id}
            style={{
              ...S.float,
              left: f.x * TILE + TILE / 2,
              top: f.y * TILE - TILE * 0.4,
              color: f.color,
            }}
          >
            {f.text}
          </div>
        ))}

        {/* the player — the being, via the one continuity mount */}
        <AnnihilationPlayer
          character={character}
          pos={ctrl.pos}
          facing={ctrl.facing}
          walkFrame={anim.walkFrame}
          isIdle={anim.isIdle}
          idleFrame={anim.idleFrame}
          tile={TILE}
          opacity={ctrl.dodging || ctrl.isInvulnerable() ? 0.45 : 1}
          swing={ctrl.lastSwing}
        />
      </div>

      {feltEvent ? <div style={S.felt}>{feltEvent}</div> : null}

      <div style={S.controls}>
        move: arrows / WASD · attack: Space or J · dodge: Shift or K
      </div>

      <button style={S.back} onClick={onBack}>
        ← back to Sigfer's trees
      </button>

      <MobileDpad keysRef={ctrl.keysRef} />
      <MobileActionPad onAttack={ctrl.attack} onDodge={ctrl.dodge} />
    </div>
  );
}

function Bar({ label, value, max, color }) {
  return (
    <div style={S.statBar}>
      <span style={S.statBarLabel}>{label}</span>
      <div style={S.statBarTrack}>
        <div
          style={{
            ...S.statBarFill,
            width: `${Math.max(0, Math.min(100, (value / max) * 100))}%`,
            background: color,
          }}
        />
      </div>
      <span style={S.statBarNum}>
        {Math.ceil(value)}/{max}
      </span>
    </div>
  );
}

const gold = "#e8a33d";
const S = {
  void: {
    minHeight: "100vh",
    background:
      "radial-gradient(ellipse at 50% 30%, #14101c 0%, #0a0810 55%, #050408 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "16px 10px 120px",
    boxSizing: "border-box",
    fontFamily: "Georgia, 'Times New Roman', serif",
    overflow: "hidden",
  },
  hud: { width: "100%", maxWidth: YARD_W, marginBottom: 10 },
  hudTitle: {
    color: gold,
    fontSize: 18,
    letterSpacing: "0.12em",
    textAlign: "center",
    marginBottom: 8,
    textShadow: "0 0 18px rgba(232,163,61,0.3)",
  },
  statBar: { display: "flex", alignItems: "center", gap: 8, marginBottom: 5 },
  statBarLabel: {
    color: "#8a7a5c",
    fontSize: 10,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    width: 44,
    textAlign: "right",
  },
  statBarTrack: {
    flex: 1,
    height: 10,
    background: "rgba(0,0,0,0.5)",
    border: "1px solid rgba(232,163,61,0.25)",
    borderRadius: 999,
    overflow: "hidden",
  },
  statBarFill: { height: "100%", transition: "width 0.15s" },
  statBarNum: { color: "#b5a284", fontSize: 11, width: 64 },
  lytRow: { display: "flex", alignItems: "center", gap: 5, marginBottom: 8 },
  lytLabel: {
    color: "#8a7a5c",
    fontSize: 10,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    width: 44,
    textAlign: "right",
  },
  lytPip: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    border: `1px solid rgba(232,163,61,0.5)`,
    background: "rgba(0,0,0,0.4)",
  },
  lytPipFull: { background: gold, boxShadow: "0 0 8px rgba(232,163,61,0.5)" },
  comboText: { color: "#8fd18a", fontSize: 11, marginLeft: 8, fontStyle: "italic" },
  barHolder: { marginTop: 4 },
  yard: {
    position: "relative",
    border: "1px solid rgba(232,163,61,0.35)",
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 0 40px rgba(232,163,61,0.08), inset 0 0 60px rgba(0,0,0,0.6)",
    maxWidth: "100%",
  },
  ground: {
    position: "absolute",
    inset: 0,
    background:
      "repeating-linear-gradient(0deg, #17131f 0, #17131f 47px, #1d1827 47px, #1d1827 48px)," +
      "repeating-linear-gradient(90deg, #17131f 0, #17131f 47px, #1d1827 47px, #1d1827 48px)",
    opacity: 0.9,
  },
  dummy: {
    position: "absolute",
    width: TILE,
    height: TILE * 1.6,
    zIndex: 8,
    transition: "filter 0.2s",
  },
  dummyTelegraph: { filter: "drop-shadow(0 0 10px #e86a5c) brightness(1.4)" },
  dummyPost: {
    position: "absolute",
    left: "42%",
    top: "30%",
    width: "16%",
    height: "70%",
    background: "#6b4a2f",
    borderRadius: 3,
  },
  dummyHead: {
    position: "absolute",
    left: "30%",
    top: "6%",
    width: "40%",
    height: "26%",
    background: "#a3792f",
    borderRadius: "50%",
    border: "2px solid #7a5a22",
  },
  dummyArms: {
    position: "absolute",
    left: "8%",
    top: "38%",
    width: "84%",
    height: "12%",
    background: "#8a6a3a",
    borderRadius: 4,
  },
  dummyHp: {
    position: "absolute",
    left: "-10%",
    top: "-16%",
    width: "120%",
    height: 6,
    background: "rgba(0,0,0,0.6)",
    border: "1px solid rgba(232,106,92,0.4)",
    borderRadius: 999,
    overflow: "hidden",
  },
  dummyHpFill: { height: "100%", background: "#e86a5c", transition: "width 0.12s" },
  bleedTag: {
    position: "absolute",
    left: "-20%",
    bottom: "-22%",
    color: "#e86a5c",
    fontSize: 10,
    whiteSpace: "nowrap",
  },
  dummyDown: {
    position: "absolute",
    color: "#6d5f47",
    fontSize: 22,
    zIndex: 8,
  },
  float: {
    position: "absolute",
    zIndex: 30,
    fontSize: 15,
    fontWeight: 700,
    textShadow: "0 1px 3px #000",
    animation: "none",
    transform: "translate(-50%, -100%)",
    transition: "top 0.9s ease-out, opacity 0.9s",
    pointerEvents: "none",
  },
  felt: {
    position: "fixed",
    top: "18%",
    left: "50%",
    transform: "translateX(-50%)",
    color: gold,
    fontSize: 15,
    fontStyle: "italic",
    textShadow: "0 0 16px rgba(232,163,61,0.5)",
    zIndex: 120,
    pointerEvents: "none",
  },
  controls: { color: "#6d5f47", fontSize: 11, marginTop: 10, textAlign: "center" },
  back: {
    background: "transparent",
    border: "1px solid rgba(232,163,61,0.5)",
    color: gold,
    padding: "9px 20px",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.05em",
    marginTop: 12,
  },
};
